import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { HelpDrawer } from "@/components/ui/help-drawer";
import { DiscountPoliciesEditor } from "./DiscountPoliciesEditor";
import { DiscountWorkflowsEditor } from "./DiscountWorkflowsEditor";
import {
  FINANCIAL_PROFILES_QUERY_KEY,
  clonePolicies,
  cloneWorkflows,
  discountPoliciesSchema,
  discountWorkflowsSchema,
  normalizePolicy,
  normalizeWorkflow,
  serializePolicy,
  serializeWorkflow,
  type DiscountPolicyFormValue,
  type DiscountWorkflowFormValue,
  type FinancialProfilesPayload,
  type FinancialProfilesResponse,
} from "./financialProfiles.helpers";
import { fetchFinancialProfiles, updateFinancialProfiles } from "@/lib/api/settings";
import financialProfilesHelp from "@/../docs/help/financial-profiles.md?raw";

type ValidationIssue = {
  scope: "policy" | "workflow";
  path: string;
  message: string;
};

type NormalizedState = {
  policies: DiscountPolicyFormValue[];
  workflows: DiscountWorkflowFormValue[];
};
const computeStateHash = (state: NormalizedState): string =>
  JSON.stringify({
    policies: state.policies,
    workflows: state.workflows,
  });

const formatIssuePath = (segments: (string | number)[]): string => {
  if (!segments.length) {
    return "(root)";
  }
  return segments
    .map((segment) => (typeof segment === "number" ? `[${segment}]` : segment))
    .join(".");
};

type FinancialProfilesManagerProps = {
  onSaved?: () => void;
};

export function FinancialProfilesManager({ onSaved }: FinancialProfilesManagerProps): JSX.Element {
  const queryClient = useQueryClient();
  const [policies, setPolicies] = useState<DiscountPolicyFormValue[]>([]);
  const [workflows, setWorkflows] = useState<DiscountWorkflowFormValue[]>([]);
  const [baselineHash, setBaselineHash] = useState<string>("");
  const [taxProfiles, setTaxProfiles] = useState<FinancialProfilesResponse["taxProfiles"]>([]);
  const [tipProfiles, setTipProfiles] = useState<FinancialProfilesResponse["tipProfiles"]>([]);

  const financialProfilesQuery = useQuery({
    queryKey: FINANCIAL_PROFILES_QUERY_KEY,
    queryFn: ({ signal }) => fetchFinancialProfiles(signal),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!financialProfilesQuery.data) {
      return;
    }

    const normalizedPolicies = clonePolicies(financialProfilesQuery.data.discountPolicies.map(normalizePolicy));
    const normalizedWorkflows = cloneWorkflows(financialProfilesQuery.data.discountWorkflows.map(normalizeWorkflow));

    setPolicies(normalizedPolicies);
    setWorkflows(normalizedWorkflows);
    setTaxProfiles(financialProfilesQuery.data.taxProfiles);
    setTipProfiles(financialProfilesQuery.data.tipProfiles);
    setBaselineHash(
      computeStateHash({
        policies: clonePolicies(normalizedPolicies),
        workflows: cloneWorkflows(normalizedWorkflows),
      }),
    );
  }, [financialProfilesQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (payload: FinancialProfilesPayload) => updateFinancialProfiles(payload),
    onSuccess: (response) => {
      const normalizedPolicies = clonePolicies(response.discountPolicies.map(normalizePolicy));
      const normalizedWorkflows = cloneWorkflows(response.discountWorkflows.map(normalizeWorkflow));

      setPolicies(normalizedPolicies);
      setWorkflows(normalizedWorkflows);
      setTaxProfiles(response.taxProfiles);
      setTipProfiles(response.tipProfiles);
      setBaselineHash(
        computeStateHash({
          policies: clonePolicies(normalizedPolicies),
          workflows: cloneWorkflows(normalizedWorkflows),
        }),
      );

      toast.success("Financial profiles updated", {
        description: "Discount policies and workflows saved successfully.",
      });

      queryClient.invalidateQueries({ queryKey: FINANCIAL_PROFILES_QUERY_KEY });

      if (onSaved) {
        onSaved();
      }
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : "We couldn't update the financial profiles. Please try again in a moment.";

      toast.error("Failed to update financial profiles", {
        description: message,
      });
    },
  });

  const currentHash = useMemo(
    () =>
      computeStateHash({
        policies: policies,
        workflows: workflows,
      }),
    [policies, workflows],
  );

  const policyValidation = useMemo(() => discountPoliciesSchema.safeParse(policies), [policies]);
  const workflowValidation = useMemo(() => discountWorkflowsSchema.safeParse(workflows), [workflows]);

  const validationIssues = useMemo<ValidationIssue[]>(() => {
    const issues: ValidationIssue[] = [];

    if (!policyValidation.success) {
      for (const issue of policyValidation.error.issues) {
        issues.push({
          scope: "policy",
          path: formatIssuePath(issue.path),
          message: issue.message,
        });
      }
    }

    if (!workflowValidation.success) {
      for (const issue of workflowValidation.error.issues) {
        issues.push({
          scope: "workflow",
          path: formatIssuePath(issue.path),
          message: issue.message,
        });
      }
    }

    return issues;
  }, [policyValidation, workflowValidation]);

  const hasValidationErrors = validationIssues.length > 0;
  const isDirty = baselineHash.length > 0 && currentHash !== baselineHash;
  const isLoading = financialProfilesQuery.isLoading && !financialProfilesQuery.data;

  const handleReset = () => {
    if (!financialProfilesQuery.data) {
      return;
    }

    const normalizedPolicies = clonePolicies(financialProfilesQuery.data.discountPolicies.map(normalizePolicy));
    const normalizedWorkflows = cloneWorkflows(financialProfilesQuery.data.discountWorkflows.map(normalizeWorkflow));

    setPolicies(normalizedPolicies);
    setWorkflows(normalizedWorkflows);
    setBaselineHash(
      computeStateHash({
        policies: clonePolicies(normalizedPolicies),
        workflows: cloneWorkflows(normalizedWorkflows),
      }),
    );
  };

  const handleSave = async () => {
    const payload: FinancialProfilesPayload = {
      taxProfiles,
      tipProfiles,
      discountPolicies: policies.map(serializePolicy),
      discountWorkflows: workflows.map(serializeWorkflow),
    };

    await saveMutation.mutateAsync(payload);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (financialProfilesQuery.isError) {
    const message =
      financialProfilesQuery.error instanceof Error
        ? financialProfilesQuery.error.message
        : "We couldn't load the financial profiles.";

    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Unable to load financial profiles</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{message}</p>
          <Button size="sm" variant="outline" onClick={() => financialProfilesQuery.refetch()}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Discount policies & workflows</h3>
          <p className="text-sm text-muted-foreground">
            Manage guardrails for discount approvals. Changes save immediately to the tenant profile.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <HelpDrawer
            title="Financial profiles guide"
            description="Learn how policies, workflows, and approvals stay in sync."
            markdown={financialProfilesHelp}
            analyticsId="financial-profiles-help"
            triggerLabel="How approvals work"
          />
          <Badge
            variant={hasValidationErrors ? "destructive" : "outline"}
            className={hasValidationErrors ? "bg-destructive/15 text-destructive" : "bg-emerald-500/10 text-emerald-700"}
          >
            {hasValidationErrors ? "Validation issues" : "Configuration valid"}
          </Badge>
          <Badge variant="secondary" className="bg-muted text-muted-foreground">
            {policies.length} {policies.length === 1 ? "policy" : "policies"}, {workflows.length}{" "}
            {workflows.length === 1 ? "workflow" : "workflows"}
          </Badge>
        </div>
      </div>

      {validationIssues.length > 0 ? (
        <Alert variant="destructive" className="border-destructive/40 bg-destructive/5">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Resolve validation issues before saving</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 space-y-1 text-sm">
              {validationIssues.slice(0, 10).map((issue, index) => (
                <li key={`${issue.scope}-${issue.path}-${index}`}>
                  <span className="font-medium capitalize">{issue.scope}</span> {issue.path}: {issue.message}
                </li>
              ))}
              {validationIssues.length > 10 ? (
                <li className="text-xs text-muted-foreground">
                  {validationIssues.length - 10} additional issue(s) not shown.
                </li>
              ) : null}
            </ul>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-emerald-500/25 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Policies ready</AlertTitle>
          <AlertDescription>
            All discount policies and workflows pass schema validation. Publish to apply changes for operators.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={handleReset} disabled={!isDirty || saveMutation.isLoading}>
          Reset changes
        </Button>
        <Button
          onClick={handleSave}
          disabled={!isDirty || hasValidationErrors || saveMutation.isLoading}
          className="gap-2"
        >
          {saveMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {saveMutation.isLoading ? "Saving…" : "Save policies & workflows"}
        </Button>
      </div>

      <Card className="border-border/60 shadow-sm">
        <CardHeader className="border-b border-border/60">
          <CardTitle className="text-base">Discount policies</CardTitle>
          <CardDescription>Policy schema updates enforce stacking rules, approvals, and reason codes.</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <DiscountPoliciesEditor value={policies} onChange={(next) => setPolicies(clonePolicies(next))} />
        </CardContent>
      </Card>

      <Separator />

      <Card className="border-border/60 shadow-sm">
        <CardHeader className="border-b border-border/60">
          <CardTitle className="text-base">Approval workflows</CardTitle>
          <CardDescription>Configure approvers, thresholds, escalate contacts, and decline requirements.</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <DiscountWorkflowsEditor value={workflows} onChange={(next) => setWorkflows(cloneWorkflows(next))} />
        </CardContent>
      </Card>
    </div>
  );
}
